
// Implementación actualizada del componente AddVaccine
import { AppBackground, Button, Drop, Input, Text, ResponsiveLogo, BottomActionContainer } from '@ui/components'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReactComponent as ChevronDownIcon } from '@/assets/icons/aena/aena_chevron_down.svg'
import { ReactComponent as XSimpleIcon } from '@/assets/icons/x_simple_grey.svg'
import {
  DatePickerWrapper,
  InputContainer,
  InputWrapper,
  ModalContainer,
  ModalHeader,
  Headline5,
  DisabledButton,
  ButtonContent,
  DisabledButtonText,
  ErrorText,
} from '@/aena/styles'
import { colors } from '@/aena/styles'
import { CamaraCard, ImageTypeSelector } from '@/components/cameracard'
import DatePicker from 'react-datepicker'
import { getDatePickerLocale } from '@/utils/datepickerLocale'
import { loadActualVaccineUrl, useAppDispatch, useAppSelector } from '@front/store'
import { FormikValues } from 'formik'
import { usePetVaccines } from '@front/logic'
import { View, TouchableOpacity, Modal } from 'react-native'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { t } from 'i18next'
import { ModalWrapper } from '@/aena/styles'
import { SwitchSelector } from '@/components/switchselector'

const option1 = 'MANDATORY'
const option2 = 'OPTIONAL'

const AddVaccine: React.FC = () => {

  const vaccines = useAppSelector((state) => state.petDocumentation.vaccines)
  const mandatoryVaccines = useAppSelector((state) => state.petDocumentation.mandatoryVaccines)
  const actualVaccineUrl = useAppSelector((state) => state.petDocumentation.actualVaccineUrl)
  const [vaccineType, setVaccineType] = useState<'MANDATORY' | 'OPTIONAL'>('MANDATORY')
  const [showModal, setShowModal] = useState(false)
  const [date, setDate] = useState<Date | undefined>(undefined)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const onNextPress = (values: FormikValues) => {
    // REFRESCO DEL HISTORIAL tras guardar:
    // Al volver con navigate(-1) regresamos al listado (./index.tsx -> <Vaccines/>).
    // Ese listado pinta la imagen leyendo appliedVaccines del store, que se rellena
    // en usePetVaccines.getVaccines() -> getPetVaccinesAPI -> getPetVaccinesFromNeon
    // (packages/apis/src/Services/aenaPassportNeon.ts), donde cada history[].file
    // se resuelve con fileUrl(item) (storage_ref/url de petpass_file).
    // OJO: <Vaccines/> sólo llama getVaccines() en su useEffect(_, []) de montaje, y
    // con navigate(-1) NO se vuelve a montar, por lo que la lista puede quedar STALE.
    // El detalle (./history.tsx) además lee desde useLocation().state (snapshot del
    // momento de navegar), así que depende de que el listado esté ya refrescado.
    // Si la imagen no aparece tras añadir, forzar el refetch en el listado al recibir
    // foco (p.ej. re-ejecutar getVaccines en <Vaccines/>), no aquí.
    navigate(-1)
  }

  const {
    setFieldValue,
    values,
    handleBlur,
    handleChange,
    errors,
    handleSubmit,
    getVaccines,
    touched,
    setTouched,
    validateForm,
    dirty,
  } = usePetVaccines(onNextPress)

  // Indica si el usuario ya intentó enviar el formulario al menos una vez.
  // A partir de ese momento mostramos el resumen de campos obligatorios que faltan.
  const [submitAttempted, setSubmitAttempted] = useState(false)

  useEffect(() => {
    console.debug('ERRORS', errors)
  }, [errors])

  useEffect(() => {
    if (vaccines.length <= 0) {
      getVaccines()
    }
  }, [vaccines])

  // Resetear la selección de vacuna cuando cambia el tipo
  useEffect(() => {
    setFieldValue('vaccineGuid', '')
  }, [vaccineType, setFieldValue])

  const handleSubmitForm = async () => {
    // await setFieldValue('vaccineDate', date)
    setSubmitAttempted(true)
    if (canSubmit && actualVaccineUrl) {
      handleSubmit()
      return
    }
    // Formulario inválido: NO enviamos. Marcamos todos los campos obligatorios como
    // "touched" y forzamos la validación para que se muestre el error inline de cada
    // campo vacío (además del resumen visible junto al botón).
    setTouched({
      vaccineUrl: true,
      vaccineGuid: true,
      vaccineLote: true,
      vaccineDate: true,
    })
    await validateForm()
  }

  const handleDateChange = (date: Date | null) => {
    // console.log('DATE', date?.toISOString())
    if (date) {
      setDate(date)
      setFieldValue('vaccineDate', date)
    }
  }

  const onGalleryPress = (file: File, preview: string) => {
    setFieldValue('vaccineUrl', file)
    dispatch(loadActualVaccineUrl(preview))
  }

  const handlePreviewPress = () => {
    setShowModal(!showModal)
  }

  const handleVaccineTypeChange = (type: 'MANDATORY' | 'OPTIONAL') => {
    setVaccineType(type)
  }

  // Obtener las vacunas filtradas según el tipo seleccionado
  const getFilteredVaccines = () => {
    return vaccineType === 'MANDATORY' 
      ? mandatoryVaccines 
      : vaccines.filter(v => v.type !== "MANDATORY")
  }

  const filteredVaccines = getFilteredVaccines()

  const canSubmit = useMemo(() => {
    return values.vaccineUrl && values.vaccineGuid && values.vaccineLote && date
  }, [values,date])

  // Lista de campos OBLIGATORIOS que aún faltan por rellenar, usando los mismos
  // textos i18n que las etiquetas de cada campo. Sirve para el resumen visible.
  const missingFields = useMemo(() => {
    const missing: string[] = []
    if (!values.vaccineUrl) missing.push(t('common.photo'))
    if (!values.vaccineGuid) missing.push(t('vaccines.vaccine'))
    if (!values.vaccineLote) missing.push(t('common.lotreference'))
    if (!date) missing.push(t('common.vaccinationappointment'))
    return missing
  }, [values.vaccineUrl, values.vaccineGuid, values.vaccineLote, date])

  // Mostramos el resumen cuando el usuario ya empezó a rellenar (tocó/cambió algún
  // campo o subió la foto) o cuando ya intentó enviar el formulario.
  const hasStarted = dirty || !!actualVaccineUrl
  const showMissingSummary = (hasStarted || submitAttempted) && missingFields.length > 0

  // Decide si mostrar el error inline de un campo obligatorio: cuando formik ya tiene
  // error para ese campo, o cuando está vacío y el usuario ya empezó a rellenar / ya
  // tocó el campo / ya intentó enviar (objetivo: no esperar a tocar cada campo).
  const showFieldError = (
    field: 'vaccineUrl' | 'vaccineGuid' | 'vaccineLote' | 'vaccineDate',
    isEmpty: boolean,
  ) => {
    if (errors[field]) return true
    return isEmpty && (hasStarted || submitAttempted || !!touched[field])
  }

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={t('common.addvaccine')} />
        <BottomActionContainer>
          {/* Fragment: BottomActionContainer.children es ReactElement(s); el Fragment
              recibe un único hijo y dentro acepta comentarios JSX y condicionales. */}
          <>
          <View style={{flex:1}}>
            {/* Selector de tipo de vacuna */}
            <SwitchSelector
              option1={option1}
              option2={option2}
              tOption1={t('common.addmandatory') || 'Obligatorias'}
              tOption2={t('common.addoptional') || 'Opcionales'}
              selectedType={vaccineType}
              onTypeChange={handleVaccineTypeChange}
            />
            {/* La doc de Air Europa limita los requisitos de viaje a la antirrábica;
                el resto son recomendaciones veterinarias, no exigidas por la aerolínea. */}
            {vaccineType === 'OPTIONAL' && (
              <Text type="caption" color="foreground5" style={{ marginTop: 8, marginBottom: 4, textAlign: 'center' }}>
                Vacunas recomendadas por tu veterinario · no exigidas por Air Europa para viajar.
              </Text>
            )}
            <InputWrapper>
            <Headline5>{t('common.photo')} *</Headline5>
            <InputContainer>
              <CamaraCard
                title={t('common.choosephoto')}
                subtitle={t('common.uploadvaccinationcard')}
                onGalleryPress={onGalleryPress}
                url={actualVaccineUrl}
                type={ImageTypeSelector.VACCINE}
              />
              {showFieldError('vaccineUrl', !values.vaccineUrl) && (
                <ErrorText>
                  {errors.vaccineUrl?.toString() || 'Campo obligatorio'}
                </ErrorText>
              )}
            </InputContainer>
            </InputWrapper>
            <InputWrapper>
              <Headline5>{t('vaccines.vaccine')} *</Headline5>
            <InputContainer>
              <Drop
                buttonText={t('common.continue')}
                placeholder={t('common.selectvaccine')}
                icon={<ChevronDownIcon width={22} height={22} />}
                activeColor="#96ce00"
                data={filteredVaccines.map((vaccine) => ({
                  title: t(`vaccines.${vaccine.tname}`),
                  id: vaccine.guid,
                  data: { ...vaccine },
                }))}
                active={filteredVaccines.findIndex(
                  (obj) => obj.guid === values.vaccineGuid,
                )}
                selectedItem={filteredVaccines.findIndex(
                  (obj) => obj.guid === values.vaccineGuid,
                )}
                onPress={(item) => {
                  setFieldValue('vaccineGuid', item.id)
                }}
                allowIconAndText
                variant="outlined"
                color="aena"
              />
              {showFieldError('vaccineGuid', !values.vaccineGuid) ? (
                <ErrorText>
                  {errors.vaccineGuid?.toString() || 'Campo obligatorio'}
                </ErrorText>
              ) : (
                <></>
              )}
            </InputContainer>
            </InputWrapper>

            <InputWrapper>
              <Headline5>{t('common.lotreference')} *</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.vaccineLote,
                    onChangeText: handleChange('vaccineLote'),
                    onBlur: handleBlur('vaccineLote'),
                    placeholder: t('common.lot'),
                  }}
                />
                {showFieldError('vaccineLote', !values.vaccineLote) && (
                  <ErrorText>
                    {errors.vaccineLote?.toString() || 'Campo obligatorio'}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>

            <InputWrapper >
              <Headline5>{t('common.vaccinationappointment')} *</Headline5>
              <InputContainer >
                <DatePickerWrapper>
                  <DatePicker
                    selected={date}
                    date={date}
                    onChange={handleDateChange}
                    // onBlur={handleBlur('vaccineDate')}
                    dateFormat="dd/MM/yyyy"
                    placeholderText={t('common.selectadate')}
                    className="datepicker-styled"
                    popperPlacement="bottom-start"
                    dropdownMode="select"
                    maxDate={new Date()}
                    locale={getDatePickerLocale()}
                    showMonthDropdown
                    showYearDropdown
                  />
                </DatePickerWrapper>
                {showFieldError('vaccineDate', !date) && (
                  <ErrorText>
                    {errors.vaccineDate?.toString() || 'Campo obligatorio'}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>

          </View>
          {showMissingSummary && (
            <View
              style={{
                backgroundColor: '#fff3f3',
                borderColor: '#ff4b4b',
                borderWidth: 1,
                borderRadius: 8,
                padding: 12,
                marginBottom: 12,
              }}
            >
              <ErrorText>
                {'Faltan por rellenar: ' + missingFields.join(', ')}
              </ErrorText>
            </View>
          )}
          {/* El botón "Añadir" siempre es pulsable: cuando el formulario es inválido
              conserva el aspecto deshabilitado, pero al pulsarlo handleSubmitForm marca
              los campos como touched y muestra el resumen de lo que falta (no envía). */}
          {canSubmit ? (
              <Button
                title={t('common.add')}
                size="block"
                textType="cta_medium"
                borderRadius={5}
                buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
                textColor="white"
                color="primary"
                onPress={handleSubmitForm}
              />
            ) : (
              <DisabledButton onPress={handleSubmitForm} activeOpacity={0.7}>
                <ButtonContent>
                  <DisabledButtonText>{t('common.add')}</DisabledButtonText>
                </ButtonContent>
              </DisabledButton>
            )
          }
          {/* <Button
            title={t('common.add')}
            buttonWrapperStyle={{ backgroundColor: colors.aenaVerde }}
            icon={<PawIcon width={24} height={24} />}
            size="block"
            textType="cta_medium"
            borderRadius={5}
            textColor="background0"
            allowIconAndText
            color="primary"
            onPress={handleSubmitForm}
          /> */}
          </>
        </BottomActionContainer>
        <Modal
          visible={showModal}
          transparent={true}
          onRequestClose={handlePreviewPress}
        >
          <ModalWrapper>  
            <ModalContainer>
              <ModalHeader>
                <Text type="H5">{t('common.preview')}</Text>
                <TouchableOpacity onPress={handlePreviewPress}>
                  <XSimpleIcon width={30} height={30} />
                </TouchableOpacity>
              </ModalHeader>
              <img src={actualVaccineUrl} style={{ width: 300, height: 300 }} />
            </ModalContainer>
          </ModalWrapper>
        </Modal>        
      </View>
    </AppBackground>
  )
}

export default AddVaccine

// export { VaccineTypeSelector }
